/**
 * Pure browser API interceptors
 *
 * This module provides low-level browser API interception utilities.
 * NO BUSINESS LOGIC should be in this file - it's pure infrastructure.
 *
 * Business logic (URL transformations, flags, etc.) should be injected
 * via callbacks from the services layer.
 */

const nativeOpenKey = '_nativeOpen';

/**
 * Receives the ORIGINAL target (before any TargetTransformer runs) so the
 * transformer can distinguish same-frame app navigation ('_self'/'_parent'/
 * '_top') from opens that leave the app ('_blank'/undefined).
 */
export type UrlTransformer = (url: string, originalTarget?: string) => string;
export type TargetTransformer = (target?: string) => string | undefined;

/**
 * Creates a window.open() interceptor with custom URL transformation
 *
 * @param urlTransformer - Function to transform URLs before opening
 * @param targetTransformer - Optional function to transform the target parameter
 *
 * @example
 * ```typescript
 * createWindowOpenInterceptor(transformUrl, transformTarget);
 * ```
 */
export const createWindowOpenInterceptor = (
    urlTransformer: UrlTransformer,
    targetTransformer?: TargetTransformer,
): void => {
    const nativeOpen = window.open;

    const customOpen: typeof window.open = (
        url?: string | URL,
        target?: string,
        features?: string,
    ) => {
        // URL decision is made on the ORIGINAL target — a TargetTransformer
        // may downgrade '_blank' to '_self' (disableRedirectionLinksInNewTab),
        // and that downgrade must not change whether the URL is rewritten.
        const transformedUrl = url ? urlTransformer(String(url), target) : url;
        const transformedTarget = targetTransformer
            ? targetTransformer(target)
            : target;
        return nativeOpen(transformedUrl, transformedTarget, features);
    };

    window.open = customOpen;
    (window as any)[nativeOpenKey] = nativeOpen;
};

/**
 * Restores the native window.open() implementation
 */
export const restoreWindowOpen = (): void => {
    const nativeOpen = (window as any)[nativeOpenKey];
    if (nativeOpen) {
        window.open = nativeOpen;
        delete (window as any)[nativeOpenKey];
    }
};
